import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  FormControl,
  Select,
  InputLabel,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import {
  Logout,
  Person,
  Menu as MenuIcon,
  PersonOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import ProfileModal from './ProfileModal';
import { listConfigProfiles } from '../api/configProfiles';
import { useTranslation } from 'react-i18next';
import axiosClient from '../api/axiosClient';

interface HeaderProps {
  onMenuToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [profileOpen, setProfileOpen] = useState(false);
  const [profiles, setProfiles] = useState<Array<{ name: string }>>([]);
  const [activeProfile, setActiveProfile] = useState<string | ''>('');

  // Actualizar el reloj cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Cargar perfiles y perfil activo
  useEffect(() => {
    (async () => {
      try {
        const saved = localStorage.getItem('activeConfigProfile') || '';
        setActiveProfile(saved);
        const list = await listConfigProfiles();
        setProfiles(list.map(p => ({ name: p.name })));
      } catch (e) {
        console.warn('No se pudieron cargar perfiles', e);
      }
    })();
  }, []);

  // Sincronizar el estado cuando cambia el localStorage (desde otra pestaña o actualización)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'activeConfigProfile') {
        setActiveProfile(e.newValue || '');
      }
    };

    // Escuchar cambios en el localStorage desde otras pestañas
    window.addEventListener('storage', handleStorageChange);

    // También escuchar cambios locales (cuando se actualiza desde el mismo componente)
    const handleLocalStorageChange = () => {
      const saved = localStorage.getItem('activeConfigProfile') || '';
      if (saved !== activeProfile) {
        setActiveProfile(saved);
      }
    };

    // Verificar periódicamente si cambió el localStorage (para cambios locales)
    const interval = setInterval(handleLocalStorageChange, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [activeProfile]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const handleProfileOpen = () => {
    setProfileOpen(true);
    handleMenuClose();
  };

  const handleProfileClose = () => {
    setProfileOpen(false);
  };

  const formatTime = (date: Date) => {
    const localeMap: Record<string, string> = { en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR' };
    const locale = localeMap[i18n.language] || 'en-US';
    return date.toLocaleString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!user) return null;

  return (
    <AppBar position="static" color="primary" elevation={2}>
      <Toolbar>
        {/* Botón hamburguesa */}
        <IconButton
          color="inherit"
          aria-label="abrir menú"
          onClick={onMenuToggle}
          sx={{ 
            mr: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Título de la aplicación y menú */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {t('header.appTitle')}
          </Typography>
          <Typography variant="subtitle2" component="div" sx={{ fontWeight: 500, opacity: 0.9, lineHeight: 1.2 }}>
            {t('header.menu')}
          </Typography>
          <Typography variant="caption" component="div" sx={{ opacity: 0.8, lineHeight: 1.2 }}>
            {user.username}
          </Typography>
        </Box>
        
        {/* Espaciador para centrar el reloj */}
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Selector de idioma */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
          <LanguageIcon sx={{ mr: 1, opacity: 0.85 }} />
          <FormControl size="small">
            <InputLabel id="lang-label">{t('header.language')}</InputLabel>
            <Select
              labelId="lang-label"
              label={t('header.language')}
              value={i18n.language.split('-')[0]}
              onChange={async (e) => {
                const lang = String(e.target.value);
                await i18n.changeLanguage(lang);
                try { localStorage.setItem('preferredLanguage', lang); } catch {}
                // Opcional: guardar preferencia en backend si hay usuario
                try {
                  if (user?.id) {
                    // Usar axiosClient para que incluya el header X-Config-Profile
                    await axiosClient.put('/users/me', { preferredLanguage: lang });
                  }
                } catch {}
              }}
              sx={{ minWidth: 120 }}
              native
            >
              {/* Using native select for compactness */}
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
            </Select>
          </FormControl>
        </Box>

        {/* Selector de perfil activo */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3, minWidth: 220 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="config-profile-label">{t('header.profile')}</InputLabel>
            <Select
              labelId="config-profile-label"
              label={t('header.profile')}
              value={activeProfile}
              onChange={(e) => {
                const v = String(e.target.value);
                setActiveProfile(v);
                try { localStorage.setItem('activeConfigProfile', v); } catch {}
                // No forzamos reload; las siguientes peticiones ya enviarán el header
              }}
            >
              {profiles.map(p => (
                <MenuItem key={p.name} value={p.name}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Reloj en tiempo real */}
        <Typography 
          variant="body1" 
          sx={{ 
            mr: 3,
            fontWeight: 500,
            textAlign: 'center',
            minWidth: '300px'
          }}
        >
          {formatTime(currentTime)}
        </Typography>
        
        {/* Botón de usuario */}
        <IconButton
          size="large"
          edge="end"
          aria-label="cuenta de usuario"
          aria-controls="user-menu"
          aria-haspopup="true"
          onClick={handleMenuOpen}
          color="inherit"
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
            <Person />
          </Avatar>
        </IconButton>

        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem disabled>
            <Box>
              <Typography variant="subtitle2" color="text.primary">
                {user.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('header.authenticated')}
              </Typography>
            </Box>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleProfileOpen}>
            <PersonOutline sx={{ mr: 1 }} />
            {t('header.myProfile')}
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1 }} />
            {t('header.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
      
      {/* Modal de perfil */}
      <ProfileModal open={profileOpen} onClose={handleProfileClose} />
    </AppBar>
  );
};

export default Header;
